import { Injectable } from '@angular/core'
import { STRIPE_PUBLIC_KEY } from '../../constants'
import { Coupon, CouponData, CouponError, CouponRequest } from '../../interfaces'
import { FirebaseService } from './../firebase.service'
import { Logger } from './../logger.service'
import { default as StripeBE } from 'stripe';
import { LoaderService } from './../loader.service'
import { Subject, BehaviorSubject } from 'rxjs';
import { today } from '../../functions';
import { PaymentIntent, PaymentMethod, StripeCardElement, ConfirmCardSetupData, Stripe } from '@stripe/stripe-js'
import { pairwise } from 'rxjs/operators'
import { firestore } from 'firebase'
import { checkIsKonbiniPayment } from './functions'
import { DB_GIFT_PAYMENTS, DB_PAYMENTS, DB_PAYMENT_METHODS, DB_COUPONS, DB_STRIPE_CUSTOMERS } from '@functions/constants'

const stripe = new Promise<Stripe>((resolve) => {
  // ! do not download the stripe file !
  const script = document.createElement('script');

  script.onload = () => {
    resolve(window.Stripe(STRIPE_PUBLIC_KEY));
  };

  script.src = 'https://js.stripe.com/v3/';

  document.head.appendChild(script);
});

type PaymentDataPayment = Partial<PaymentIntent>;

type TPaymentCollection = typeof DB_PAYMENTS | typeof DB_GIFT_PAYMENTS;

export interface PaymentData {
  id: string,
  payment: PaymentDataPayment,
}

// const logStyle = 'color: #5469d4;'
const log = new Logger('StripeService')

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  public readonly stripe = stripe;

  public readonly elements = stripe.then((_stripe) => _stripe.elements());

  private _currentOrderIdSubject = new BehaviorSubject<string | undefined>(undefined);

  private _currentGiftOrderIdSubject = new BehaviorSubject<string | undefined>(undefined);

  /**
   * could be set from outside, **to handle payment events for current order**.
   *
   * *if not set - service will not handle or complete any payment.*
   */
  set currentOrderId(id: string | undefined) {
    this._currentOrderIdSubject.next(id);
  };

  get currentOrderId(): string | undefined {
    return this._currentOrderIdSubject.value;
  };

  set currentGiftOrderId(id: string | undefined) {
    this._currentGiftOrderIdSubject.next(id);
  };

  get currentGiftOrderId(): string | undefined {
    return this._currentGiftOrderIdSubject.value;
  };

  /** fires on first load from db  */
  public paymentLoaded = new Subject<PaymentData>();

  /** fires on every update in db */
  public paymentUpdated = new Subject<PaymentData>();

  /** fires if payment succeeded in db  */
  public paymentSucceeded = new Subject<PaymentData>();

  /**
   * fires when `currentOrderId` or `currentGiftOrderId` payment action's handled
   * TODO: pass TPaymentCollection to recognize payment
   */
  public paymentActionHandle = new Subject<void>();

  // * GIFT
  /** fires on first load from db  */
  public giftPaymentLoaded = new Subject<PaymentData>();

  /** fires on every update in db */
  public giftPaymentUpdated = new Subject<PaymentData>();

  /** fires if payment succeeded in db  */
  public giftPaymentSucceeded = new Subject<PaymentData>();

  // TODO: change to public
  // private creatingPayment = false

  // * Coupons
  /** coupons map where `key` is `orderId` */
  public coupons: {
    [orderId: string]: CouponData;
  } = {};

  public couponAdd = new Subject<{
    [orderId: string]: Coupon | CouponRequest;
  }>();

  public couponUpdate = new Subject<{
    [orderId: string]: CouponData;
  }>();

  public couponError = new Subject<{
    [orderId: string]: CouponError;
  }>();

  // TODO: change to public
  // private redeemingCoupon = false

  // * Payment Methods
  private paymentMethods = new Map<
    string, PaymentMethod
  >();

  // triggers on every update
  private paymentMethodsUpdate = new Subject<PaymentMethod>();

  // TODO: change to public
  // private addingCard = false

  // * Payments
  private payments = new Map<string, PaymentDataPayment>();

  private giftPayments = new Map<string, PaymentDataPayment>();

  private _customerData: any;

  constructor(
    private _firebaseService: FirebaseService,
    private _loaderService: LoaderService,
  ) {
    this._init();
  }

  get customerData(): any {
    return this._customerData;
  }

  get currentOrderIdPayment(): PaymentDataPayment | undefined {
    return this.currentOrderId && this.payments.get(this.currentOrderId);
  }

  get currentGiftOrderIdPayment(): PaymentDataPayment | undefined {
    return this.currentGiftOrderId && this.giftPayments.get(this.currentGiftOrderId);
  }

  private async _init() {
    log.info('init');
    const user = await this._firebaseService.userAuth()

    log.info('user got', user);
    // Firebase auth configuration
    this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .onSnapshot((snapshot) => {
        const customerData = snapshot.data()
        if (customerData) {
          log.info(`Stripe customer found in Firestore for user: ${user.uid}`, customerData)
          this._customerData = customerData
          this._initDataListeners()
        } else {
          log.info(`No Stripe customer found in Firestore for user: ${user.uid}`)
        }
      })
  }

  /** applies coupon to the order by `couponId` */
  public async redeemCoupon(
    orderId: string, couponId: string
  ) {
    log.info('redeemCoupon', { orderId, couponId })
    const user = await this._firebaseService.userAuth()

    this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(DB_COUPONS)
      .doc(orderId)
      .set({ id: couponId })
  }

  public async getCardPaymentIntentCreateParams(
    card?: StripeCardElement,
    name?: string,
    pmToken?: string,
  ): Promise<Partial<StripeBE.PaymentIntentCreateParams>> {
    if (pmToken) {
      return {
        payment_method: pmToken,
        off_session: false,
        confirm: true,
        confirmation_method: 'manual',
      };
    } else if (card) {
      return this._addCardPaymentMethod(card, name).then(token => ({ payment_method: token }));
    } else {
      log.error('card or pmToken not provided', {
        card,
        name,
        pmToken,
      });
    }
  }

  public async payWithCard(
    orderId: string,
    card?: StripeCardElement,
    name?: string,
    pmToken?: string,
    collection: TPaymentCollection = DB_PAYMENTS
  ): Promise<void> {
    const paymentIntentCreateParams = await this.getCardPaymentIntentCreateParams(card, name, pmToken);

    if (!paymentIntentCreateParams) return;

    return this._createPayment(
      orderId,
      paymentIntentCreateParams,
      collection,
    );
  }

  public getKonbiniPaymentIntentCreateParams(
    name: string,
    email: string,
    phone: string,
  ): Partial<StripeBE.PaymentIntentCreateParams> {
    return {
      payment_method_data: {
        // @ts-ignore
        type: 'konbini',
        billing_details: {
          name,
          email,
          phone,
        },
      },
      payment_method_types: ['konbini'],
      payment_method_options: {
        // @ts-ignore
        konbini: {
          product_description: '絵本',
          // expires_after_days: 2,
          expires_at: today().add(2, 'days').add(5, 'minutes').unix(),
          confirmation_number: phone,
        },
      },
    }
  }

  public async payWithKonbini(
    orderId: string,
    name: string,
    email: string,
    phone: string,
    collection: TPaymentCollection = DB_PAYMENTS,
  ): Promise<void> {
    return this._createPayment(
      orderId,
      this.getKonbiniPaymentIntentCreateParams(name, email, phone),
      collection,
    );
  }

  private async _initDataListeners() {
    const user = await this._firebaseService.userAuth();

    // * Get all coupons for the logged in customer
    this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(DB_COUPONS)
      .onSnapshot((snapshot) => snapshot.forEach(doc => {
        this._couponsUpdateHandler(doc);
      }));

    // * Get all payment methods for the logged in customer
    this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(DB_PAYMENT_METHODS)
      .onSnapshot(snapshot => snapshot.forEach(doc => {
        this._paymentMethodsUpdateHandler(doc);
      }));

    // * Get all payments for the logged in customer
    this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(DB_PAYMENTS)
      .onSnapshot((snapshot) => snapshot.forEach(doc => {
        const payment = doc.data() as PaymentDataPayment;

        this._paymentsUpdateHandler(doc.id, payment);
      }));

    // * Get all payments for the logged in customer
    this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(DB_GIFT_PAYMENTS)
      .onSnapshot((snapshot) => snapshot.forEach(doc => {
        const payment = doc.data() as PaymentDataPayment;

        this._giftPaymentsUpdateHandler(doc.id, payment);
      }));

    this._currentOrderIdSubject.pipe(
      pairwise(),
    ).subscribe(([prevOrderId, orderId]) => {
      /** check order id change to trigger order specific handlers */
      if (orderId && prevOrderId !== orderId) {
        const payment = this.payments.get(orderId);

        if (payment) {
          this._orderSpecificPaymentHandler(orderId, payment, DB_PAYMENTS);
        }
      }
    });

    this._currentGiftOrderIdSubject.pipe(
      pairwise(),
    ).subscribe(([prevOrderId, orderId]) => {
      /** check order id change to trigger order specific handlers */
      if (orderId && prevOrderId !== orderId) {
        const payment = this.giftPayments.get(orderId);

        if (payment) {
          this._orderSpecificPaymentHandler(orderId, payment, DB_GIFT_PAYMENTS);
        }
      }
    });
  }

  // Handle card actions like 3D Secure
  private async _handleCardAction(
    payment: PaymentDataPayment,
    docId: string,
    collection: TPaymentCollection,
  ) {
    const user = await this._firebaseService.userAuth();

    const { error, paymentIntent } = await (await stripe).handleCardAction(
      payment.client_secret,
    );

    if (error) {
      alert(error.message)
      payment = error.payment_intent;
    } else if (paymentIntent) {
      payment = paymentIntent;
    }

    await this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(collection)
      .doc(docId)
      .set(payment, { merge: true });

    this.paymentActionHandle.next();
  }

  private async _handleKonbiniAction(
    payment: PaymentDataPayment,
    docId: string,
    collection: TPaymentCollection,
  ) {
    const user = await this._firebaseService.userAuth();

    const { error, paymentIntent } = await (await stripe).confirmKonbiniPayment(
      payment.client_secret,
    );

    if (error) {
      alert(error.message)
      payment = error.payment_intent;
    } else if (paymentIntent) {
      payment = paymentIntent;
    }

    await this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(collection)
      .doc(docId)
      .set(payment, { merge: true });

    this.paymentActionHandle.next();
  }

  // Method to add new card into user`s customerData
  private async _addCardPaymentMethod(
    card: StripeCardElement,
    name?: PaymentMethod.BillingDetails['name']
  ): Promise<PaymentMethod['id']> {
    log.info('_addCardPaymentMethod: start');
    // this.addingCard = true

    const paymentMethod: ConfirmCardSetupData['payment_method'] = {
      card,
    };

    if (name) {
      paymentMethod.billing_details = {
        name,
      };
    }

    const { setupIntent, error } = await (await stripe).confirmCardSetup(
      this.customerData.setup_secret,
      { payment_method: paymentMethod },
    )

    if (error) {
      // this.addingCard = false
      log.error('_addCardPaymentMethod: error', error.message);
      throw error;
    }

    await this._createPaymentMethod(setupIntent.payment_method as string);

    // this.addingCard = false
    log.info('_addCardPaymentMethod: success');
    return setupIntent.payment_method as string;
  }

  private async _createPaymentMethod(
    id: string,
  ): Promise<void> {
    const user = await this._firebaseService.userAuth();

    await this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(DB_PAYMENT_METHODS)
      .add({ id });

    log.info('_createPaymentMethod: success')
  }

  private async _createPayment(
    orderId: string,
    paymentIntentCreateParams: Partial<StripeBE.PaymentIntentCreateParams>,
    collection: TPaymentCollection,
  ): Promise<void> {
    log.info('_createPayment: start', {
      orderId, paymentIntentCreateParams
    })

    // this.creatingPayment = true

    if (!orderId) {
      console.error('orderId is not provided')
      return
    }

    const user
      = await this._firebaseService.userAuth()

    await this._firebaseService.db
      .collection(DB_STRIPE_CUSTOMERS)
      .doc(user.uid)
      .collection(collection)
      .doc(orderId)
      .set(paymentIntentCreateParams)

    // this.creatingPayment = false
    log.info('createPayment: success')
  }

  private _couponsUpdateHandler(
    doc: firestore.QueryDocumentSnapshot<
      firestore.DocumentData
    >,
  ): void {
    const coupon = doc.data() as CouponData
    const orderId = doc.id

    const previousCoupon = this.coupons[orderId]
    this.coupons[orderId] = coupon

    log.info(`coupon [${orderId}] ${
      previousCoupon ? 'updated' : 'added'
    }${
      'error' in coupon ? ` with error ${coupon.error}` : ''
    }`, coupon)

    if (previousCoupon)
      this.couponUpdate.next({[orderId]: coupon})
    else
      this.couponAdd.next({[orderId]: coupon})

    if ('error' in coupon)
      this.couponError.next({[orderId]: coupon})
  }

  private _paymentMethodsUpdateHandler(
    doc: firestore.QueryDocumentSnapshot<
      firestore.DocumentData
    >,
  ): void {
    const paymentMethod = doc.data() as PaymentMethod

    if (!paymentMethod.card) return // idk why c: it was in doc

    const previousPaymentMethod = this.paymentMethods.get(doc.id)
    this.paymentMethods.set(doc.id, paymentMethod)

    const content = `[payment method ${doc.id}] ${paymentMethod.card.brand} •••• ${paymentMethod.card.last4} | Expires ${paymentMethod.card.exp_month}/${paymentMethod.card.exp_year}`

    if (!previousPaymentMethod)
      log.info(content, this.paymentMethods)
    else
      log.debug(content, this.paymentMethods)

    if (previousPaymentMethod)
      this.paymentMethodsUpdate.next(paymentMethod)
  }

  private _paymentsUpdateHandler(
    id: string,
    payment: PaymentDataPayment,
  ): void {
    const previousPayment = this.payments.get(id);
    const isStatusUpdated: boolean = previousPayment && (previousPayment.status !== payment.status);

    this.payments.set(id, payment);

    if ((!previousPayment || isStatusUpdated) && this.currentOrderId === id) {
      this._orderSpecificPaymentHandler(id, payment, DB_PAYMENTS);
    }

    // events
    if (payment.status === 'succeeded') {
      this.paymentSucceeded.next({ id, payment });
    }

    if (previousPayment) {
      this.paymentUpdated.next({ id, payment });
    } else {
      this.paymentLoaded.next({ id, payment });
    }
  }

  private _giftPaymentsUpdateHandler(
    id: string,
    payment: PaymentDataPayment,
  ): void {
    const previousPayment = this.giftPayments.get(id);
    const isStatusUpdated: boolean = previousPayment && (previousPayment.status !== payment.status);

    this.giftPayments.set(id, payment);

    if ((!previousPayment || isStatusUpdated) && this.currentGiftOrderId === id) {
      this._orderSpecificPaymentHandler(id, payment, DB_GIFT_PAYMENTS);
    }

    // events
    if (payment.status === 'succeeded') {
      this.giftPaymentSucceeded.next({ id, payment });
    }

    if (previousPayment) {
      this.giftPaymentUpdated.next({ id, payment });
    } else {
      this.giftPaymentLoaded.next({ id, payment });
    }
  }

  private _orderSpecificPaymentHandler(
    id: string,
    payment: PaymentDataPayment,
    collection: TPaymentCollection,
  ): void {
    switch (payment.status) {
      case 'requires_confirmation':
        log.info(`[payment ${id}] creating`);
        break;
      case 'succeeded':
        log.info(`[payment ${id}] succeed`);
        break;
      case 'requires_action':
        log.info(`[payment ${id}] creating`);

        // if (!this._loaderService.shown) {
        //   this._loaderService.show();
        // }

        if (checkIsKonbiniPayment(payment)) {
          this._handleKonbiniAction(payment, id, collection);
        } else {
          this._handleCardAction(payment, id, collection);
        }

        break;
      default:
        log.info(`[payment ${id}] new`);
        break;
    }
  }
}
